import React from 'react'
import { useAppDispatch } from 'utils/redux'
import { DealDetail, EntityType } from 'api'
import { useModal } from 'view/shared/modal'
import { ActionItem, SplitButtonDropdown } from 'view/components/menus'
import { ActionsButtonProps } from 'view/components/datagrid'
import Ticket from '../../deals/components/DealTicket'
import {
  ServicingCloseOpenDialog,
  ServicingRerateDialog,
  ServicingRollInPlaceDialog,
  ServicingSubstitutionDialog,
  ServicingTerminationDialog,
  ServicingAdminTerminationDialog,
  ServicingAdminCloseOpenDialog,
  ServicingAdminRerateDialog,
  ServicingAdminSubstitutionDialog,
  ServicingBulkCloseOpenDialog
} from 'view/shared/servicingDialogs'
import {
  canServicingAccept,
  canServicingAdminStartSubstitute,
  canServicingSend,
  canServicingStartAdminCloseOpen,
  canServicingStartAdminRerate,
  canServicingStartAdminTerminate,
  canServicingStartCloseOpen,
  canServicingStartRerate,
  canServicingStartRollInPlace,
  canServicingStartSubstitute,
  canServicingStartTerminate,
  canViewRelatedTrades,
  getDealTitle
} from 'view/routes/deals/components/utils'
import { isOpenFin } from 'utils'
import { WindowActions } from 'epics/windowEpic'
import { Locale, Translation } from 'locale'
import { RelatedDealsThunks } from 'thunks'
import { useCommonDealActionItems } from 'view/routes/deals/components/useCommonDealActionItems'

export interface StructuredActionsButtonProps extends ActionsButtonProps<DealDetail> {
  disabled?: boolean
}

export const StructuredActionsButton = ({ disabled, items, isGroupRow }: StructuredActionsButtonProps) => {
  const { showModal } = useModal()
  const dispatch = useAppDispatch()

  const getLabel = (baseLabel: Translation) => {
    const count = isGroupRow ? ` (${items.length})` : ``
    return `${Locale.translate(baseLabel)}${count}`
  }

  const handleOpenTicket = () => {
    items.forEach(dealDetail => {
      if (isOpenFin()) {
        dispatch(WindowActions.openTicketWindow(dealDetail.deal.dealId, EntityType.DEAL, getDealTitle(dealDetail)))
      } else {
        showModal(<Ticket dealId={dealDetail.deal.dealId} />)
      }
    })
  }

  const handleServicingStartSubstitute = () => {
    const { deal } = items[0]
    if (deal.collateralId && deal.clearingHouseId === null) {
      showModal(<ServicingSubstitutionDialog dealId={deal.dealId} />)
    } else {
      throw new Error('DealServicingStartSubstituteEvent not allowed for non-bilateral or basket deals')
    }
  }

  const handleServicingAdminStartSubstitute = () => {
    const { deal } = items[0]
    showModal(<ServicingAdminSubstitutionDialog dealId={deal.dealId} />)
  }

  const handleServicingStartAdminRerate = () => {
    showModal(<ServicingAdminRerateDialog dealIds={items.map(item => item.deal.dealId)} />)
  }

  const handleServicingStartRerate = () => {
    showModal(<ServicingRerateDialog dealIds={items.map(item => item.deal.dealId)} />)
  }

  const handleServicingStartCloseOpen = () => {
    items.length === 1
      ? showModal(<ServicingCloseOpenDialog dealId={items[0].deal.dealId} />)
      : showModal(
          <ServicingBulkCloseOpenDialog canSend={canServicingSend} dealIds={items.map(item => item.deal.dealId)} adminCloseOpen={false} />
        )
  }

  const handleServicingStartAdminCloseOpen = () => {
    items.length === 1
      ? showModal(<ServicingAdminCloseOpenDialog dealId={items[0].deal.dealId} />)
      : showModal(
          <ServicingBulkCloseOpenDialog canSend={canServicingAccept} dealIds={items.map(item => item.deal.dealId)} adminCloseOpen={true} />
        )
  }

  const handleServicingStartTerminate = () => {
    showModal(<ServicingTerminationDialog dealIds={items.map(item => item.deal.dealId)} />)
  }

  const handleServicingStartAdminTerminate = () => {
    showModal(<ServicingAdminTerminationDialog dealIds={items.map(item => item.deal.dealId)} />)
  }

  const handleServicingStartRollInPlace = () => {
    showModal(<ServicingRollInPlaceDialog dealIds={items.map(item => item.deal.dealId)} />)
  }

  const handleViewRelatedTradesClick = () => {
    dispatch(RelatedDealsThunks.updateStructuredRelatedDealsSubscription(items[0].deal.originalDealId))
  }

  const commonActions = useCommonDealActionItems(items)
  const actions: ActionItem[] = [
    {
      id: Translation.TC_ACTION_TERMINATE,
      label: getLabel(Translation.TC_ACTION_TERMINATE),
      onClick: handleServicingStartTerminate,
      enabled: canServicingStartTerminate(items)
    },
    {
      id: Translation.TC_ACTION_TERMINATE,
      label: getLabel(Translation.TC_ACTION_TERMINATE),
      onClick: handleServicingStartAdminTerminate,
      enabled: canServicingStartAdminTerminate(items)
    },
    {
      id: Translation.TC_ACTION_RERATE,
      label: getLabel(Translation.TC_ACTION_RERATE),
      onClick: handleServicingStartRerate,
      enabled: canServicingStartRerate(items)
    },
    {
      id: Translation.TC_ACTION_RERATE,
      label: getLabel(Translation.TC_ACTION_RERATE),
      onClick: handleServicingStartAdminRerate,
      enabled: canServicingStartAdminRerate(items)
    },
    {
      id: Translation.TC_ACTION_CLOSE_OPEN,
      label: getLabel(Translation.TC_ACTION_CLOSE_OPEN),
      onClick: handleServicingStartCloseOpen,
      enabled: canServicingStartCloseOpen(items)
    },
    {
      id: Translation.TC_ACTION_CLOSE_OPEN,
      label: getLabel(Translation.TC_ACTION_CLOSE_OPEN),
      onClick: handleServicingStartAdminCloseOpen,
      enabled: canServicingStartAdminCloseOpen(items)
    },
    {
      id: Translation.TC_ACTION_SUBSTITUTE,
      label: getLabel(Translation.TC_ACTION_SUBSTITUTE),
      onClick: handleServicingStartSubstitute,
      enabled: items.length === 1 && canServicingStartSubstitute(items[0])
    },
    {
      id: Translation.TC_ACTION_SUBSTITUTE,
      label: getLabel(Translation.TC_ACTION_SUBSTITUTE),
      onClick: handleServicingAdminStartSubstitute,
      enabled: items.length === 1 && canServicingAdminStartSubstitute(items[0])
    },
    {
      id: Translation.TC_ACTION_ROLL_IN_PLACE,
      label: getLabel(Translation.TC_ACTION_ROLL_IN_PLACE),
      onClick: handleServicingStartRollInPlace,
      enabled: canServicingStartRollInPlace(items)
    },
    {
      id: Translation.TC_ACTION_OPEN_TICKET,
      label: Locale.translate(Translation.TC_ACTION_OPEN_TICKET),
      onClick: handleOpenTicket,
      enabled: true
    },
    {
      label: getLabel(Translation.TC_VIEW_RELATED_TRADES),
      onClick: handleViewRelatedTradesClick,
      enabled: canViewRelatedTrades(items)
    },
    ...commonActions
  ]

  const actionIds = actions.filter(action => action.enabled).map(mainAction => mainAction.id)
  const maxButtons = actionIds.includes(Translation.TC_ACTION_TERMINATE) && actionIds.includes(Translation.TC_ACTION_RERATE) ? 2 : 1

  return <SplitButtonDropdown actions={actions} disabled={disabled} maxButtons={maxButtons} dataTestId="OpenRepos" />
}
